import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SiteService } from '../services/site.service';
import { PlatformService } from '../services/platform.service';
import { environment } from '../../../environments/environment';
import { isInPlaceSessionRecoveryPath } from '../utils/session-recovery-path';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private injector: Injector,
    private siteService: SiteService,
    private router: Router,
    private platformService: PlatformService,
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    const isLoginEndpoint = req.url.includes('/auth/login');
    const isRefreshEndpoint = req.url.includes('/auth/refresh');
    const isSiteBootstrap = req.url.includes('/site?host=');
    const isLogoutEndpoint = req.url.includes('/auth/logout');
    const isAuthFlow = isLoginEndpoint || isRefreshEndpoint || isSiteBootstrap || isLogoutEndpoint;

    // Pre-flight: a locally-expired token is guaranteed a 401, so don't send the
    // request at all — go straight to the (coalesced) refresh flow. A returning
    // user's dashboard fires several parallel calls; without this every one of
    // them hit the server and failed individually (the Monday-morning 401 waves).
    if (token && !isAuthFlow && this.authService.isTokenExpired(token)) {
      return this.handleAuthError(req, next, 401);
    }

    let authReq = req;
    // Relative '/api/...' URLs are our backend too: the fleet CloudFront /api/*
    // behavior forwards them to api.location.pro WITHOUT the browser's Host
    // header, so the backend's tenant check can only learn the tenant from
    // x-site-host. Treat them as API calls so they carry the same headers.
    const isExternalApi =
      req.url.includes(environment.apiUrl) ||
      req.url.includes('api.location.pro') ||
      req.url.startsWith('/api/');

    if (!isAuthFlow && !req.headers.has('Authorization')) {
      if (token) {
        authReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
      }
    }

    // ── ALWAYS attach X-Requested-With to every request, not just external API.
    // Previously this was gated behind isExternalApi which meant relative-path
    // calls like /api/v1/reservation/all (no scheme, no environment.apiUrl
    // prefix) bypassed the header and triggered Chrome's native Basic auth
    // dialog when the server returned 401 with WWW-Authenticate: Basic.
    //
    // X-Requested-With on every request is a no-op for our own backend
    // (which uses Bearer tokens) and tells any well-behaved server / proxy
    // not to send Basic auth challenges that pop the Chrome native dialog.
    //
    // Also: if there is no Bearer token AND no Authorization header, set a
    // sentinel Bearer value. Chrome will not pop the Basic dialog when it
    // sees the request already carried an Authorization header of a
    // different scheme - the server will reject our sentinel with 401 and
    // we handle it cleanly through catchError below.
    {
      let headers = authReq.headers;
      if (!headers.has('X-Requested-With')) {
        headers = headers.set('X-Requested-With', 'XMLHttpRequest');
      }
      if (!isAuthFlow && !headers.has('Authorization')) {
        headers = headers.set('Authorization', 'Bearer none');
      }
      authReq = authReq.clone({ headers });
    }

    // Prevent browser/proxy from caching API data responses
    if (isExternalApi && (req.method === 'POST' || req.method === 'GET')) {
      let headers = authReq.headers;
      if (!headers.has('Cache-Control')) {
        headers = headers.set('Cache-Control', 'no-cache, no-store');
        headers = headers.set('Pragma', 'no-cache');
      }
      authReq = authReq.clone({ headers });
    }

    // Add site identification headers for API calls.
    // Skip site bootstrap requests — adding x-site-host routes them to the public
    // external handler which never returns Ext.token. Without this header the request
    // reaches the authenticated route that calls getSiteByHost() and returns the token.
    if (isExternalApi && !isSiteBootstrap) {
      // getRequestHost/Hostname mirror window.location.host/.hostname exactly
      // in the browser (same values, same precedence below) and fall back to
      // the SSR request's Host header on the server — see SiteService.
      const isLocalhost = this.siteService.getRequestHostname() === 'localhost' || this.siteService.getRequestHostname() === '127.0.0.1';
      const siteHost = isLocalhost ? environment.defaultSite : (this.siteService.getCurrentSite()?.host || this.siteService.getRequestHost());
      let headers = authReq.headers;
      if (!headers.has('x-site-host')) {
        headers = headers.set('x-site-host', siteHost);
      }
      authReq = authReq.clone({ headers });
    }

    // Send credentials (cookies) for auth endpoints so httpOnly refresh token is included
    if (isRefreshEndpoint || isLoginEndpoint) {
      authReq = authReq.clone({ withCredentials: true });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // The public shared-budget page answers a missing/wrong share password
        // with 401 (no data). That is not an expired session: never run the
        // refresh/redirect-to-login recovery for it.
        const isPublicBudgetShare = /\/budget-share\/(view|download)/.test(req.url);
        if (error.status === 401 && !isAuthFlow && !isPublicBudgetShare) {
          return this.handleAuthError(req, next, error.status);
        }
        return throwError(() => error);
      })
    );
  }

  // Timestamp of the last session-expired redirect. Used as a debounce so concurrent
  // 401s don't each trigger a redirect, but resets naturally — unlike a plain boolean
  // that was never cleared and caused the second expiry in the same SPA session to be ignored.
  private lastSessionExpiredMs = 0;

  private handleSessionExpired(): void {
    const now = Date.now();
    const firstInWindow = now - this.lastSessionExpiredMs >= 5000;

    // Clear the token BEFORE emitting so the page's handler sees
    // isAuthenticated() === false and can show the recovery banner/modal.
    // The old order (emit then clear) caused the handler's live-token
    // self-heal to swallow the first-time event — the JWT was still locally
    // valid even though the server had definitively rejected the refresh.
    if (firstInWindow) {
      this.lastSessionExpiredMs = now;
      this.isRefreshing = false;
      this.authService.clearAuthToken();
    }

    // Emit ALWAYS (NM 2026-08-13): a save on /incentive fires several calls
    // at once; suppressing burst followers used to swallow the event the
    // page's in-place recovery depends on. The handler's own self-heal
    // (auth.helpers onSessionExpired) returns early when the token is live
    // (David Cottingham class), so emitting is safe even on a stale event.
    this.authService.emitSessionExpired();

    if (!firstInWindow) return;
    // Everything below is a browser-tab navigation/reload recovery — there is
    // no tab to redirect during SSR, and scheduling a setTimeout on the
    // server would just leak for the life of the Node process.
    if (!this.platformService.isBrowser) return;

    setTimeout(() => {
      const path = window.location.pathname;
      // Dead-session incident (2026-07-17, KY /incentive): a KY applicant was
      // mid-application when this reload fired. The reload itself killed the
      // page — auth/me and auth/refresh both came back 401 post-reload — and
      // left the form silently dead; document uploads failed 32 times with
      // zero server requests before he happened to log back in. A forced
      // `window.location.reload()` on a form with unsaved work (including
      // File objects held for a retry queue, which cannot survive a reload
      // at all) is exactly the wrong recovery: it destroys the very thing it
      // should be protecting.
      //
      // Project-registration (/incentive) now owns its own in-place recovery:
      // it listens to sessionExpired$ (above), keeps the form on screen, and
      // surfaces the header's sign-in modal with a "session expired" message
      // (see ProjectRegistrationComponent.sessionExpired /
      // HeaderComponent.loginContextMessage). No reload needed there.
      //
      // Admin (/admin) gets the SAME in-place treatment as of PR-C
      // (2026-07-20, session idle timeout + heartbeat): AdminLayoutComponent
      // now mounts a chromeless `<app-header [loginOnly]="true">` purely to
      // keep its ngOnInit subscription to loginModalRequested$ alive, so the
      // header's existing sign-in modal can surface on top of any admin page
      // without navigating away from it. Before this line existed, ANY 401
      // on ANY admin API call (not just this PR's heartbeat) fell through to
      // the `else` branch below and hard-navigated the admin user to
      // /locations 100ms later — discarding whatever admin page/state they
      // were on. That is the exact hazard class this file's own class doc
      // exists to prevent, just never closed for /admin. Minimal diff:
      // adding the same "handled in-place, don't reload/redirect" opt-out
      // /incentive already has, to the one additional path this PR's
      // HeartbeatService needs it for. See session-recovery-path.ts for why
      // this check is a separately unit-tested pure function.
      const reloadPaths = ['/permits', '/register', '/project', '/permit'];
      if (isInPlaceSessionRecoveryPath(path)) {
        return; // handled in-place by the page — see comment above
      }
      if (reloadPaths.some(p => path.startsWith(p))) {
        window.location.reload();
      } else {
        window.location.href = '/locations';
      }
    }, 100);
  }

  private resetRefreshState(): void {
    this.isRefreshing = false;
    // Emit '' to unblock any calls queued on refreshSubject — they
    // receive the empty-string sentinel and fail gracefully instead of hanging
    this.refreshSubject.next('');
    this.refreshSubject.next(null); // reset back to null for next cycle
  }

  private handleAuthError(req: HttpRequest<any>, next: HttpHandler, status: number): Observable<HttpEvent<any>> {
    // isUserLoggedIn() checks the in-memory userSubject. Also check localStorage so
    // permit/tax users (who store their session in project_user_session) and users whose
    // JWT just expired on load (userSubject is null until /auth/me returns) are covered.
    const wasUserLoggedIn = this.authService.isUserLoggedIn()
      || (this.platformService.isBrowser && (!!localStorage.getItem('project_user_session') || !!localStorage.getItem('lp_permit_token')));

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.authService.refreshAccessToken().pipe(
        switchMap(newToken => {
          if (newToken) {
            this.isRefreshing = false;
            this.refreshSubject.next(newToken);
            const retryReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${newToken}`) });
            return next.handle(retryReq);
          }

          // refreshAccessToken() returned null — the refresh cookie is expired.
          // For logged-in users, redirect to login immediately. Previously this fell
          // through to refreshFreeToken(), which would retry with a public-only token,
          // still get 401 on auth-required endpoints, and leave the dashboard blank
          // with no error or redirect (the root cause of the silent data-loss bug).
          if (wasUserLoggedIn) {
            this.resetRefreshState();
            this.handleSessionExpired();
            return throwError(() => new Error('Session expired.'));
          }

          return this.siteService.refreshFreeToken().pipe(
            switchMap(freeToken => {
              this.isRefreshing = false;
              if (freeToken) {
                this.refreshSubject.next(freeToken);
                const retryReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${freeToken}`) });
                return next.handle(retryReq);
              }
              this.resetRefreshState();
              return throwError(() => new Error('Session expired.'));
            }),
            catchError(() => {
              this.resetRefreshState();
              return throwError(() => new Error('Session expired.'));
            })
          );
        }),
        catchError((refreshErr: unknown) => {
          // A 401/403 from the refresh endpoint is DEFINITIVE (revoked family,
          // reuse detection) — retrying can never succeed. Treating it as
          // transient left the session wedged until a manual cache clear
          // (Cleveland profile lockup, 2026-07-10). Only network/5xx errors
          // are transient.
          const status = (refreshErr as { status?: number })?.status ?? 0;
          if (wasUserLoggedIn && (status === 401 || status === 403)) {
            this.resetRefreshState();
            this.handleSessionExpired();
            return throwError(() => new Error('Session expired.'));
          }
          // Transient (network, 5xx): fail this request but keep the session so
          // a blip doesn't log the user out. The next API call retries.
          if (wasUserLoggedIn) {
            this.resetRefreshState();
            return throwError(() => new Error('Temporary error, please retry.'));
          }
          return this.siteService.refreshFreeToken().pipe(
            switchMap(freeToken => {
              this.isRefreshing = false;
              if (freeToken) {
                this.refreshSubject.next(freeToken);
                const retryReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${freeToken}`) });
                return next.handle(retryReq);
              }
              this.resetRefreshState();
              return throwError(() => new Error('Session expired.'));
            }),
            catchError(() => {
              this.resetRefreshState();
              return throwError(() => new Error('Session expired.'));
            })
          );
        })
      );
    }

    return this.refreshSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        // Empty string is the failure sentinel — don't retry, just fail
        if (!token) return throwError(() => new Error('Session expired.'));
        const retryReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
        return next.handle(retryReq);
      })
    );
  }
}
